import { ConnectionBanner, Spinner } from "@omony/ui";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { api } from "../../shared/api.js";
import { releaseAllMedia } from "../../shared/media.js";
import { queryClient, refreshSession, type SessionInfo } from "../../shared/query.js";
import { useRealtime, type RealtimeEvent } from "../../shared/realtime.js";
import { Console } from "../Console.js";
import styles from "../console.module.css";
import { useDevice, usePatientView } from "../data.js";
import { markJoined } from "../flow/actions.js";
import { useIdle } from "../hooks.js";
import { useVideo, VideoProvider } from "../../shared/video.js";
import { LockScreen } from "./LockScreen.js";
import { PatientMode } from "./PatientMode.js";
import { PinChangeScreen } from "./PinChangeScreen.js";

/** Gekoppelter Kiosk: gesperrt ↔ Personal ↔ Patientenmodus (ARCHITEKTUR §10.5) */
export function KioskApp({ session }: { session: SessionInfo | null }) {
  return (
    <VideoProvider onJoined={markJoined}>
      <KioskInner session={session} />
    </VideoProvider>
  );
}

const VIDEO = new Set(["angenommen", "in_behandlung"]);

/** Sperren: Server-Sitzung beenden, Cache leeren, Kamera stoppen, Objekt-URLs freigeben */
const KEEP = new Set(["session", "device", "features", "video-url"]);

export async function lockDevice() {
  await api("/device/lock", { method: "POST" }).catch(() => undefined);
  releaseAllMedia();
  // Erst die Sitzung neu laden (Konsole verschwindet), dann alle Falldaten aus dem Cache entfernen
  await refreshSession();
  queryClient.removeQueries({ predicate: (q) => !KEEP.has(String(q.queryKey[0])) });
}

function KioskInner({ session }: { session: SessionInfo | null }) {
  const intl = useIntl();
  const video = useVideo();
  const device = useDevice(true);
  const staff = session?.kind === "staff" ? session : null;
  const [unlockRequested, setUnlockRequested] = useState(false);
  const [lastPin, setLastPin] = useState<string | null>(null);
  const [live, setLive] = useState<{ caseId: string; patientLanguage: string } | null>(null);
  const [ended, setEnded] = useState<{ patientLanguage: string } | null>(null);
  const [events, setEvents] = useState<RealtimeEvent[]>([]);
  const connection = useRealtime(true, (e) => setEvents((l) => [...l.slice(-19), e]));
  const pv = usePatientView(!staff);
  const pvCase = pv.data?.case ?? null;

  // Gesprächsende im Patientenmodus erkennen (Vorgang verschwindet aus der Patientenansicht)
  if (!staff) {
    if (pvCase && VIDEO.has(pvCase.status)) {
      if (live?.caseId !== pvCase.caseId || ended) {
        setLive({ caseId: pvCase.caseId, patientLanguage: pvCase.patientLanguage });
        setEnded(null);
      }
    } else if (pv.isFetched && live) {
      setEnded({ patientLanguage: live.patientLanguage });
      setLive(null);
    }
  }
  // Nach dem Gespräch den Video-iFrame entfernen
  useEffect(() => {
    if (ended) video.setCaseId(null);
  }, [ended, video]);

  const lock = async () => {
    setUnlockRequested(false);
    setLastPin(null);
    await lockDevice();
  };
  useIdle(device.data?.lockSeconds ?? 120, () => void lock(), !!staff);

  if (!device.data)
    return (
      <div className={styles.center}>
        <Spinner label={intl.formatMessage({ id: "common.loading" })} size={32} />
      </div>
    );

  let content;
  if (staff?.mustChangePin) content = <PinChangeScreen currentPin={lastPin} onLock={() => void lock()} />;
  else if (staff)
    content = (
      <Console
        mode="staff"
        userName={staff.displayName}
        pharmacyName={device.data.pharmacy.name}
        connection={connection}
        events={events}
        onLock={() => void lock()}
      />
    );
  else if (!unlockRequested && ((pvCase && VIDEO.has(pvCase.status)) || ended))
    content = (
      <PatientMode
        view={pvCase && VIDEO.has(pvCase.status) ? pvCase : null}
        ended={ended}
        onUnlock={() => setUnlockRequested(true)}
      />
    );
  else
    content = (
      <LockScreen
        device={device.data}
        onUnlocked={(pin) => {
          setLastPin(pin);
          setUnlockRequested(false);
          setEnded(null);
        }}
        onCancel={unlockRequested ? () => setUnlockRequested(false) : undefined}
      />
    );
  return (
    <>
      <ConnectionBanner state={connection} />
      {content}
    </>
  );
}
